import dynamic from 'next/dynamic';
import { FC } from 'react';

import Button from 'molecules/Button';
import Heading from 'molecules/Heading';
import Section from 'molecules/Section';

import Accordion from 'components/Accordion';
import ScrollFadeSection from 'components/Animations/ScrollFadeSection';
import Hero from 'components/Hero';
import ConnectorTable from 'components/Tables/Connector';

import { useScrollToHash } from 'utils/hooks/useScrollToHash';

const LazyBuilderComponent = dynamic(() =>
  import('@builder.io/react').then(mod => ({ default: mod.BuilderComponent })),
);
const ConversionPanel = dynamic(() => import('components/ConversionPanel'), { ssr: false });
const Switchback = dynamic(() => import('components/Switchback'), { ssr: false });

interface ApplicationProps {
  data: Connector;
  symbolContent?: object | null;
  symbolContentSecondary?: object | null;
}

const Application: FC<ApplicationProps> = ({ data, symbolContent, symbolContentSecondary }) => {
  const { breadcrumbTitle, titleHero, descriptionHero, aboutTitle, aboutBody, hasFaqSchema, faqTitle, faqItems } =
    data.builderExtras ?? {};

  useScrollToHash();

  return (
    <>
      <Hero
        icon={data.display.iconBase64}
        heading={titleHero?.trim() ? titleHero : `${data.display.label} Connector`}
        body={
          descriptionHero?.trim()
            ? descriptionHero
            : `<p>Quickly build integrations connecting your B2B SaaS to ${data.display.label}.</p>`
        }
        buttons={[
          {
            id: 'get-demo',
            label: 'Get a demo',
            href: '/request-a-demo/',
          },
          {
            id: 'free-trial',
            label: 'Start a free trial',
            href: '/free-trial/',
          },
        ]}
        variant="centered"
        darkMode
        hasGradientTransition
        nextComponentIsDark
        includeBreadcrumbs
        breadcrumbTitle={breadcrumbTitle?.trim() ? breadcrumbTitle : data.display.label}
      />

      <Section darkMode>
        <div className="max-w-screen-md">
          <Heading
            heading={aboutTitle?.trim() ? aboutTitle : `About the ${data.display.label} Connector`}
            body={aboutBody?.trim() ? aboutBody : data.display.description}
          >
            <Button label="Go to Docs" href={data.documentationUrl} hierarchy="primary" endIcon="arrow-right" />
          </Heading>
        </div>

        {symbolContent ? (
          <LazyBuilderComponent
            model="symbol"
            content={symbolContent}
            options={{ noTraverse: true, includeRefs: false }}
          />
        ) : null}
      </Section>

      <Section darkMode id="actions-section">
        <ScrollFadeSection delay={0.2}>
          <ConnectorTable
            title="Actions"
            items={Object.keys(data.actions).map(action => ({
              label: data.actions[action].display.label,
              description: data.actions[action].display.description,
            }))}
            tableHeadings={['Action Name', 'Action Description']}
          />
        </ScrollFadeSection>
      </Section>

      <Section darkMode id="triggers-section">
        <ScrollFadeSection delay={0.2}>
          <ConnectorTable
            title="Triggers"
            items={Object.keys(data.triggers).map(trigger => ({
              label: data.triggers[trigger].display.label,
              description: data.triggers[trigger].display.description,
            }))}
            tableHeadings={['Trigger Name', 'Trigger Description']}
          />
        </ScrollFadeSection>
      </Section>

      {(faqItems?.length ?? 0) > 0 && (
        <Section darkMode id="faq-section">
          <Accordion
            heading={faqTitle?.trim()}
            hasFaqSchema={hasFaqSchema}
            accordionItems={(faqItems ?? []).map(item => ({
              label: item.label,
              description: item.description,
            }))}
            className="connector-faq"
          />
        </Section>
      )}

      {symbolContentSecondary ? (
        <LazyBuilderComponent
          model="symbol"
          content={symbolContentSecondary}
          options={{ noTraverse: true, includeRefs: false }}
        />
      ) : (
        <Switchback
          eyebrow="API Connectors"
          heading="Integrate with customers’ other SaaS apps, fast"
          body={`<p>Prismatic’s API connectors make it easy to integrate other SaaS apps – without reinventing the wheel. Triggers, actions, and auth are fully handled. 

And they work with all of Prismatic’s options for building integrations:
</p>
<ul>
  <li>Empower non-devs with a low-code designer</li>
  <li>Give devs freedom to build completely in code</li>
  <li>Let customers build their own automations</li>
</ul>`}
          buttons={[
            {
              id: 'low-code',
              label: 'Low-code',
              href: '/platform/low-code-integration-designer/',
            },
            {
              id: 'code-native',
              label: 'Code-first',
              href: '/platform/code-native-integrations/',
            },
          ]}
          image={{
            file: {
              url: 'https://res.cloudinary.com/dcppnhptx/image/upload/v1729141616/Built-In_and_custom_connectors_v7y9en.png',
              alt: 'Built-In and custom connectors',
              width: 1184,
              height: 860,
            },
          }}
          darkMode
        />
      )}

      <ConversionPanel
        eyebrow="Get a Demo"
        heading="Ready to get started?"
        body="<p>Book a demo to see how Prismatic can help you deliver integrations fast.</p>"
        form={{
          value: {
            data: {
              formId: '6',
              variant: 'basic',
              successMessage: 'Thanks for your interest! We’ll be in touch soon.',
              legalText:
                '<p>By submitting this form, you hereby consent to our processing of your personal information in accordance with our <a href="https://prismatic.io/legal/privacy/" rel="noopener noreferrer" target="_blank" style="color: rgb(140, 225, 228);">Privacy Policy</a> , including for the purposes of sending you direct marketing communications at the email address you provided.</p>',
            },
          },
        }}
        darkMode
      />
    </>
  );
};

export default Application;
